import { useCallback, useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Plus, LayoutDashboard, Kanban, ListOrdered, List, GitFork } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { Button } from "../ui/button";
import WorkItemDialog from "./WorkItemDialog";

export function useWorkSummary(projectId) {
  const [summary, setSummary] = useState(null);
  const [sprints, setSprints] = useState([]);
  const load = useCallback(() => {
    if (!projectId) return Promise.resolve();
    return Promise.all([
      api.get(`/projects/${projectId}/work-summary`),
      api.get(`/projects/${projectId}/sprints`).catch(() => ({ data: [] })),
    ]).then(([s, sp]) => { setSummary(s.data); setSprints(sp.data); }).catch((e) => toast.error(apiError(e)));
  }, [projectId]);
  useEffect(() => { load(); }, [load]);
  return { summary, sprints, reload: load };
}

export default function ProjectTabs({ summary, sprints, onCreated, children }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (!summary) return null;
  const { project, access } = summary;
  const scrum = (project.methodology || "SCRUM") === "SCRUM";
  const tabs = [
    { to: `/projects/${project.id}`, label: t("overview"), icon: LayoutDashboard, id: "overview", end: true },
    { to: `/projects/${project.id}/board`, label: t("board"), icon: Kanban, id: "board" },
    ...(scrum ? [{ to: `/projects/${project.id}/backlog`, label: t("backlog"), icon: ListOrdered, id: "backlog" }] : []),
    { to: `/projects/${project.id}/work`, label: t("workItems"), icon: List, id: "work" },
    ...(access === "MANAGE" ? [{ to: `/projects/${project.id}/workflow`, label: t("workflow"), icon: GitFork, id: "workflow" }] : []),
  ];
  return (
    <div className="space-y-4" data-testid="project-tabs-wrap">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="min-w-0">
          <button onClick={() => navigate("/projects")} className="text-xs text-muted-foreground hover:text-foreground">{t("projects")} /</button>
          <h1 className="font-heading text-xl sm:text-2xl font-semibold tracking-tight truncate">
            <span className="text-muted-foreground mr-2">{project.key}</span>{project.name}
            <span className="ml-2 text-[10px] align-middle font-semibold border rounded px-1.5 py-0.5 text-[#334155] bg-[#F1F5F9] border-[#E2E8F0]">{project.methodology || "SCRUM"}</span>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {children}
          {access !== "READ" && (
            <Button size="sm" className="bg-[#1E3A8A] hover:bg-[#1c3479]" data-testid="project-create-item-btn" onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />{t("create")}
            </Button>
          )}
        </div>
      </div>
      <nav className="flex gap-1 border-b overflow-x-auto" data-testid="project-tabs">
        {tabs.map((tab) => (
          <NavLink key={tab.id} to={tab.to} end={tab.end} data-testid={`project-tab-${tab.id}`}
            className={({ isActive }) => `flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px whitespace-nowrap transition-colors ${
              isActive ? "border-[#1E3A8A] text-[#1E3A8A] font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <tab.icon className="h-4 w-4" />{tab.label}
          </NavLink>
        ))}
      </nav>
      <WorkItemDialog open={open} onOpenChange={setOpen} summary={summary} sprints={sprints} onCreated={onCreated} />
    </div>
  );
}
